import './Layout.scss';

import { ButtonProps, Loader, MenuItemProps, TopNavigation, TopNavigationProps } from '@trackman/web-shared-components';
import { Can, Cannot } from 'components/Can';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { AppPermissions } from 'constants/AppPermissions';
import AppSwitcher from 'components/AppSwitcher/AppSwitcher';
import { ApplicationUserRights } from 'stores/ApplicationUserRightsStore';
import { DefaultRoutes } from 'Routes';
import Forbidden from 'pages/Forbidden/Forbidden';
import LoggedOutInfoModal from 'components/LoggedOutInfoModal/LoggedOutInfoModal';
import { Notifications } from 'components/Notifications';
import ProfileIconMenu from 'components/ProfileIconMenu/ProfileIconMenu';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';

type Props = {
  children: React.ReactNode;
};

type TopLevelMenuLabel = 'Organizations' | 'B1 Units' | 'Reports';

type Navigation = Array<
  {
    label: TopLevelMenuLabel;
  } & (
  | ({
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  href: string;
  need: string[];
  submenu?: undefined;
} & ButtonProps)
  | {
  onClick?: undefined;
  href?: undefined;
  need?: undefined;
  submenu: ({
    label: string;
    onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
    href: string;
    need: string[];
  } & MenuItemProps)[];
}
  )
>;

type ValueOf<T> = T[keyof T];
type DefaultRoute = ValueOf<typeof DefaultRoutes>;

const routeToActiveLabel: Record<DefaultRoute, TopLevelMenuLabel> = {
  '/': 'Organizations',
  '/organization_reassigned': 'Organizations',
  '/users': 'Organizations',
};

const Layout = observer(({ children }: Props) => {
  const ss = useStores().startupStore;
  const history = useHistory();
  const location = useLocation();

  const [displayContent, setDisplayContent] = useState(false);

  useEffect(() => {
    if (ss.hasBearerToken) setDisplayContent(true);
  }, [ss.hasBearerToken]);

  const menuSrtucture: Navigation = useMemo(
    () => [
      {
        label: 'Organizations',
        onClick: (e) => {
          e.preventDefault();
          history.push(DefaultRoutes.OrganizationsPage);
        },
        href: DefaultRoutes.OrganizationsPage,
        need: [],
      },
    ],
    [history]
  );

  const activeNavigationLabel = useMemo(() => {
    let route = '/';
    let pathParts = location.pathname.split('/').filter((x) => x !== '')
    if(location.pathname != route){
       route += pathParts[0];
    }
    return routeToActiveLabel[route];
  }, [location.pathname]);

  const navigation: TopNavigationProps['navigation'] = useMemo(() => {
    if (!ss.hasBearerToken) return [];

    const menuSrtuctureTemp = [...menuSrtucture];

    menuSrtuctureTemp.forEach((item, index, object) => {
      if (item.need && !ApplicationUserRights.IsAllowed(item.need)) object.splice(index, 1);
      if (item.submenu) {
        item.submenu.forEach((subItem, index, object) => {
          if (!ApplicationUserRights.IsAllowed(subItem.need)) object.splice(index, 1);
        });
        if (item.submenu.length === 0) object.splice(index, 1);
      }
    });
    return menuSrtuctureTemp;
  }, [ss.hasBearerToken, menuSrtucture]);

  return (
    <div className='layout-content-container'>
      <div className='top-navigation'>
        <TopNavigation
          appName={'Admin Team Portal'}
          navigation={navigation}
          activeNavigationLabel={activeNavigationLabel}
          utilities={
            ss.hasBearerToken && !ss.isInitializing
              ? [
                { key: 'profile-icon-menu', component: <ProfileIconMenu setDisplayContent={setDisplayContent} /> },
                {
                  key: 'app-switcher-icon-menu',
                  component: <AppSwitcher setDisplayContent={setDisplayContent} />,
                },
              ]
              : []
          }
          animated
          displayContent={displayContent}
          onLogoClick={() => history.push(DefaultRoutes.OrganizationsPage)}
        />
      </div>
      {!displayContent && (
        <div className='spinner'>
          <Loader size='large' />
        </div>
      )}
      <div style={{paddingTop: '26px'}}>
        <div
          style={{
            opacity: displayContent ? 1 : 0,
            transition: 'opacity 0.3s',
            pointerEvents: displayContent ? 'auto' : 'none',
            paddingBottom: '76px',
            paddingLeft: '15px',
            paddingRight: '15px',
          }}
          className='container-fluid'
        >
          {ss.hasBearerToken && (
            <>
              <Notifications />
              <Can need={AppPermissions.PortalAccess}>{children}</Can>
              <Cannot need={AppPermissions.PortalAccess}>
                <Forbidden />
              </Cannot>
            </>
          )}
        </div>
      </div>
      {ss.config && <LoggedOutInfoModal logoutUrl={ss.config.logoutUrl} />}
    </div>
  );
});

export default Layout;
